// /home/labdoo/zitech-nexus/frontend/src/pages/admin/TrainingModulesList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminTrainingAPI } from '../../services/api';
import './TrainingManagement.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tm-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
  Clock: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

interface TrainingModule {
  id: number;
  technology_name: string;
  title: string;
  description: string;
  difficulty: string;
  duration_hours: number;
  order: number;
  is_active: boolean;
  created_at: string;
}

const TrainingModulesList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminTrainingAPI.getAllModules();
      
      if (!response.data || !Array.isArray(response.data)) {
        setError('Invalid response from server');
        setModules([]);
      } else {
        const modulesWithBooleanStatus = response.data.map((module: any) => ({
          ...module,
          is_active: module.is_active === true || module.is_active === 'true'
        }));
        setModules(modulesWithBooleanStatus);
      }
    } catch (error: any) {
      console.error('Error fetching modules:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load modules');
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this module?')) {
      try {
        await adminTrainingAPI.deleteModule(id);
        await fetchModules();
        alert('Module deleted successfully');
      } catch (error) {
        console.error('Error deleting module:', error);
        alert('Failed to delete module');
      }
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch(difficulty) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getDifficultyBackground = (difficulty: string) => {
    switch(difficulty) {
      case 'beginner': return '#d1fae5';
      case 'intermediate': return '#fef3c7';
      case 'advanced': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? '#10b981' : '#ef4444';
  };

  const getStatusBackground = (isActive: boolean) => {
    return isActive ? '#d1fae5' : '#fee2e2';
  };

  const filteredModules = filter === 'all' 
    ? modules 
    : modules.filter(m => m.technology_name === filter);

  const technologies = Array.from(new Set(modules.map(m => m.technology_name)));

  const counts = {
    all: modules.length,
    active: modules.filter(m => m.is_active).length,
    inactive: modules.filter(m => !m.is_active).length,
  };

  if (loading) {
    return (
      <div className="tm-loading-container">
        <div className="tm-spinner">
          <Icons.Spinner />
        </div>
        <p className="tm-loading-text">Loading modules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tm-error-container">
        <div className="tm-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="tm-error-title">Error Loading Modules</h2>
        <p className="tm-error-text">{error}</p>
        <div className="tm-error-actions">
          <button className="tm-btn-primary" onClick={fetchModules}>
            Retry
          </button>
          <button className="tm-btn-secondary" onClick={() => navigate('/dashboard/main')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tm-container">
      {/* Header */}
      <div className="tm-header">
        <div className="tm-header-left">
          <h1 className="tm-title">Training Modules</h1>
          <p className="tm-subtitle">Manage training modules and learning content</p>
        </div>
        <div className="tm-header-actions">
          <button 
            className="tm-btn-primary"
            onClick={() => navigate('/dashboard/admin/training/modules/create')}
          >
            <Icons.Plus />
            Create New Module
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="tm-stats-bar">
        <div className="tm-stat-item">
          <span className="tm-stat-label">Total Modules</span>
          <span className="tm-stat-value">{counts.all}</span>
        </div>
        <div className="tm-stat-divider"></div>
        <div className="tm-stat-item">
          <span className="tm-stat-label">Active</span>
          <span className="tm-stat-value tm-stat-active">{counts.active}</span>
        </div>
        <div className="tm-stat-divider"></div>
        <div className="tm-stat-item">
          <span className="tm-stat-label">Inactive</span>
          <span className="tm-stat-value tm-stat-inactive">{counts.inactive}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="tm-filter-bar">
        <button 
          className={`tm-filter-btn ${filter === 'all' ? 'tm-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
          <span className="tm-filter-count">{counts.all}</span>
        </button>
        {technologies.map(tech => (
          <button 
            key={tech}
            className={`tm-filter-btn ${filter === tech ? 'tm-filter-active' : ''}`}
            onClick={() => setFilter(tech)}
          >
            {tech}
            <span className="tm-filter-count">{modules.filter(m => m.technology_name === tech).length}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="tm-table-wrapper">
        <table className="tm-table">
          <thead>
            <tr>
              <th className="tm-col-id">ID</th>
              <th className="tm-col-title">Title</th>
              <th className="tm-col-tech">Technology</th>
              <th className="tm-col-desc">Description</th>
              <th className="tm-col-diff">Difficulty</th>
              <th className="tm-col-duration">Duration</th>
              <th className="tm-col-order">Order</th>
              <th className="tm-col-status">Status</th>
              <th className="tm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredModules.length === 0 ? (
              <tr>
                <td colSpan={9} className="tm-empty-state">
                  <Icons.Empty />
                  <p className="tm-empty-text">No training modules found</p>
                  <p className="tm-empty-subtext">Click "Create New Module" to get started</p>
                </td>
              </tr>
            ) : (
              filteredModules.map((module) => (
                <tr key={module.id} className="tm-table-row">
                  <td className="tm-col-id">
                    <span className="tm-id-badge">#{module.id}</span>
                  </td>
                  <td className="tm-col-title">
                    <span 
                      className="tm-module-title"
                      onClick={() => navigate(`/dashboard/admin/training/modules/${module.id}`)}
                    >
                      {module.title}
                    </span>
                  </td>
                  <td className="tm-col-tech">
                    <span className="tm-tech-badge">{module.technology_name}</span>
                  </td>
                  <td className="tm-col-desc">
                    <span className="tm-description-text">
                      {module.description && module.description.length > 80 
                        ? module.description.substring(0, 80) + '...' 
                        : module.description || 'No description'}
                    </span>
                  </td>
                  <td className="tm-col-diff">
                    <span 
                      className="tm-difficulty-badge"
                      style={{
                        background: getDifficultyBackground(module.difficulty),
                        color: getDifficultyColor(module.difficulty)
                      }}
                    >
                      <span className="tm-difficulty-dot" style={{ background: getDifficultyColor(module.difficulty) }}></span>
                      {module.difficulty}
                    </span>
                  </td>
                  <td className="tm-col-duration">
                    <span className="tm-duration-text">
                      <Icons.Clock />
                      {module.duration_hours} {module.duration_hours === 1 ? 'hour' : 'hours'}
                    </span>
                  </td>
                  <td className="tm-col-order">
                    <span className="tm-order-text">
                      <Icons.Calendar />
                      {module.order}
                    </span>
                  </td>
                  <td className="tm-col-status">
                    <span 
                      className="tm-status-badge"
                      style={{
                        background: getStatusBackground(module.is_active),
                        color: getStatusColor(module.is_active)
                      }}
                    >
                      <Icons.StatusDot />
                      {module.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="tm-col-actions">
                    <div className="tm-actions">
                      <button 
                        className="tm-action-btn tm-action-view"
                        onClick={() => navigate(`/dashboard/admin/training/modules/${module.id}`)}
                        title="View Contents"
                      >
                        <Icons.View />
                      </button>
                      <button 
                        className="tm-action-btn tm-action-edit"
                        onClick={() => navigate(`/dashboard/admin/training/modules/${module.id}/edit`)}
                        title="Edit"
                      >
                        <Icons.Edit />
                      </button>
                      <button 
                        className="tm-action-btn tm-action-delete"
                        onClick={() => handleDelete(module.id)}
                        title="Delete"
                      >
                        <Icons.Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrainingModulesList;